/**
 * Save something a tool produced — a chart, a .bib file, a CSV — as a file.
 *
 * Everything happens in the browser: the data never leaves the page, which is the
 * promise the tools make about privacy.
 */

export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick: Safari cancels a download whose URL is revoked
  // synchronously inside the click handler.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function downloadText(
  filename: string,
  text: string,
  mime = "text/plain;charset=utf-8"
): void {
  downloadBlob(filename, new Blob([text], { type: mime }));
}

const XML_NS = "http://www.w3.org/2000/xmlns/";
const SVG_NS = "http://www.w3.org/2000/svg";

/** Serialise an on-page `<svg>` as a standalone document. */
export function serializeSvg(svg: SVGSVGElement): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  if (!clone.getAttribute("xmlns")) {
    clone.setAttributeNS(XML_NS, "xmlns", SVG_NS);
  }
  return `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(clone)}`;
}

export function downloadSvg(filename: string, svg: SVGSVGElement): void {
  downloadText(filename, serializeSvg(svg), "image/svg+xml;charset=utf-8");
}

/**
 * Rasterise an on-page `<svg>` to a PNG.
 *
 * The SVG must be self-contained — explicit `width`/`height` (or a `viewBox`), fills
 * and fonts set as attributes or inline styles rather than inherited from page CSS —
 * because an image decoded from a blob URL sees none of the page's stylesheets.
 * `background` paints behind it, since a transparent PNG pasted into a dark document
 * shows black text on black.
 */
export async function svgToPngBlob(
  svg: SVGSVGElement,
  {
    scale = 2,
    background = "#ffffff",
  }: { scale?: number; background?: string } = {}
): Promise<Blob> {
  const box = svg.viewBox.baseVal;
  const width =
    box && box.width > 0 ? box.width : svg.width.baseVal.value || 800;
  const height =
    box && box.height > 0 ? box.height : svg.height.baseVal.value || 600;
  const source = new Blob([serializeSvg(svg)], {
    type: "image/svg+xml;charset=utf-8",
  });
  const url = URL.createObjectURL(source);
  try {
    const image = new Image();
    image.decoding = "async";
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("SVG could not be rendered"));
      image.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas is unavailable");
    }
    context.fillStyle = background;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error("PNG encoding failed")),
        "image/png"
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadPng(
  filename: string,
  svg: SVGSVGElement,
  options?: { scale?: number; background?: string }
): Promise<void> {
  downloadBlob(filename, await svgToPngBlob(svg, options));
}
